import { useMemo } from 'react';
import { useIntentBehavior } from 'seagull';
import { useLogBIEvent } from '~/components/BiFrontendProvider/useLogBIEvent';
import { useIoContext, useStore } from '~/contexts';
import { useLinkStanfordTaxServiceTemplate } from '~/hooks/reactQuery/mutations/useLinkStanfordTaxServiceTemplate';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useQueryState } from '~/hooks/reactQuery/useQueryState';
import { useReportInvalidStanfordTaxMappings } from '~/hooks/useReportInvalidStanfordTaxMappings';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { handlePromise } from '~/infra/utils';
import { selectors } from '~/store/state-selectors';
import { useStoreActions } from '~/store/store-context';
import { stanfordTaxReturnTypeOptions } from '~/utils/stanfordTaxMappingOptions';
import { buildStanfordTaxMappingRows, mappingOverlayState, resolveStanfordTaxRowChange, stanfordTaxYearSelectOptions, withSavedStanfordTaxMapping } from '../StanfordTaxMapServicesModal.utils';
const NO_SERVICE_TEMPLATES = [];
export const useStanfordTaxMapServicesModal = ()=>{
    const { t: stanfordTaxTexts } = useLocaleKeys('integrations.setup.stanfordTax');
    const serviceTemplateApi = useServiceTemplateApi();
    const { getNow } = useIoContext();
    const { api } = useStoreActions();
    const businessId = useStore(selectors.user.businessId);
    const logBiEvent = useLogBIEvent();
    const { getDisabled, getTooltip } = useIntentBehavior();
    const { linkStanfordTaxServiceTemplate } = useLinkStanfordTaxServiceTemplate();
    const { data, isLoading, error, refresh, optimisticUpdate } = useQueryState({
        queryKey: [
            QueryKeys.ServiceTemplatesForStanfordTaxMapping,
            businessId
        ],
        queryFn: ()=>serviceTemplateApi.getForMappingToStanfordTaxIntegration(),
        refreshOnHookMount: true
    });
    const serviceTemplates = data !== null && data !== void 0 ? data : NO_SERVICE_TEMPLATES;
    useReportInvalidStanfordTaxMappings(serviceTemplates);
    const isForbidden = getDisabled(Intent.StanfordTaxMapServices);
    const rows = buildStanfordTaxMappingRows(serviceTemplates, stanfordTaxTexts.mapServices);
    const returnTypeSelect = useMemo(()=>{
        const returnTypeOptions = stanfordTaxReturnTypeOptions(stanfordTaxTexts.returnTypes);
        return {
            header: stanfordTaxTexts.mapServices.returnTypeHeader(),
            selectBi: {
                name: 'stanford_tax_return_type_select'
            },
            options: ()=>returnTypeOptions,
            emptySearchText: '',
            placeholder: stanfordTaxTexts.notMapped(),
            disabled: ()=>isForbidden
        };
    }, [
        isForbidden
    ]);
    const taxYearSelect = useMemo(()=>({
            header: stanfordTaxTexts.mapServices.taxYearHeader(),
            selectBi: {
                name: 'stanford_tax_tax_year_select'
            },
            options: (row)=>stanfordTaxYearSelectOptions(getNow(), row),
            emptySearchText: '',
            placeholder: stanfordTaxTexts.notMapped(),
            disabled: (row)=>isForbidden || row.mapping === '',
            disableClearable: true
        }), [
        isForbidden
    ]);
    const retry = ()=>{
        void refresh({
            silent: false,
            throwOnError: false
        });
    };
    const saveRow = async (param)=>{
        let { row, columnId, value } = param;
        if (businessId == null) throw new Error('A StanfordTax mapping was changed outside a business');
        const mapping = resolveStanfordTaxRowChange({
            row,
            columnId,
            value,
            now: getNow()
        });
        if (mapping === undefined) {
            logBiEvent({
                type: 'click',
                entity_type: 'autocomplete',
                entity_name: 'stanford_tax_return_type_select_clear'
            });
        }
        await linkStanfordTaxServiceTemplate({
            serviceTemplateId: row.id,
            mapping
        });
        const rereadResult = await handlePromise(api.serviceTemplate.getById(businessId, row.id));
        const rereadTemplate = rereadResult.success ? rereadResult.data : undefined;
        optimisticUpdate(withSavedStanfordTaxMapping({
            templateId: row.id,
            rereadTemplate,
            mapping
        }));
    };
    return {
        rows,
        contentState: mappingOverlayState({
            isLoading,
            hasError: error != null,
            serviceTemplates
        }),
        returnTypeSelect,
        taxYearSelect,
        isForbidden,
        forbiddenTooltip: getTooltip(Intent.StanfordTaxMapServices),
        retry,
        saveRow
    };
};
